import { useState, useEffect, createContext, useContext } from 'react';
import api from '../services/api';

// Create a context for the PointInTime filter
const PointInTimeFilterContext = createContext();

// Provider component
export const PointInTimeFilterProvider = ({ children }) => {
  const [availablePointInTimes, setAvailablePointInTimes] = useState([]);
  const [selectedPointInTime, setSelectedPointInTime] = useState('All');

  // Fetch available PointInTimes from the master data
  const fetchAvailablePointInTimes = async () => {
    try {
      const response = await api.get('/point-in-times');
      const pointInTimes = response.data.data || [];
      
      // Transform to include display format
      const transformedPointInTimes = pointInTimes.map(pit => ({
        code: pit.Code,
        name: pit.Name,
        display: `${pit.Code} - ${pit.Name}` // For dropdown display
      }));
      
      setAvailablePointInTimes(transformedPointInTimes);
    } catch (error) {
      console.error('Error fetching available PointInTimes:', error);
      // Set empty array on error - no fallback values
      setAvailablePointInTimes([]);
    }
  };

  // Fetch PointInTimes when provider mounts
  useEffect(() => {
    fetchAvailablePointInTimes();
  }, []);

  // Load selected PointInTime from localStorage on mount
  useEffect(() => {
    const savedPointInTime = localStorage.getItem('selectedPointInTime');
    if (savedPointInTime) {
      setSelectedPointInTime(savedPointInTime);
    }
  }, []);

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedPointInTime', selectedPointInTime);
  }, [selectedPointInTime]);

  // Provide value to children
  const value = {
    availablePointInTimes,
    selectedPointInTime,
    setSelectedPointInTime,
    fetchAvailablePointInTimes, // Expose function to refresh PointInTimes
  };

  return (
    <PointInTimeFilterContext.Provider value={value}>
      {children}
    </PointInTimeFilterContext.Provider>
  );
};

// Custom hook to use the PointInTime filter context
export const usePointInTimeFilter = () => {
  const context = useContext(PointInTimeFilterContext);
  if (context === undefined) {
    throw new Error('usePointInTimeFilter must be used within a PointInTimeFilterProvider');
  }
  return context;
};

// Function to filter data based on PointInTime
export const filterDataByPointInTime = (data, pointInTime, pointInTimeField = 'PointInTime') => {
  if (!pointInTime || pointInTime === 'All') {
    return data;
  }
  
  return data.filter(item => item[pointInTimeField] === pointInTime);
};
